import { FC, useCallback, useEffect } from 'react';
import {
  Controller,
  ResolverOptions,
  ResolverResult,
  useForm,
} from 'react-hook-form';
import { useFetcher, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { z } from 'zod';
import { PatientIdField } from '@samd/features/orders/components/patient_id_form_field';
import { DecimalInput } from 'src/components/decimal_input';
import { IntegerInput } from 'src/components/integer_input';
import { RadioGroupField } from 'src/components/radio_group';
import { SelectList } from 'src/components/select_list';
import {
  GleasonScoreFields,
  REQUIRED_GLEASON_SCORE_FIELDS_SCHEMA,
} from 'src/distribution_platforms/features/components/gleason_score_form_fields';
import {
  emptyToUndefined,
  FormActions,
  FormFieldProps,
  processFetcherResponse,
  getProstateBiopsyTStageOptions,
  PROSTATE_BIOPSY_T_STAGES,
  safeEmptyToUndefined,
  safeParseInt,
  submitOrderAction,
} from 'src/distribution_platforms/features/order_form';
import {
  NccnRiskCategory,
  SamdOrderDetailFieldsFragment,
  OrderStatus,
  SpecimenType,
  TStage,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import {
  NccnRiskCategoryUtils,
  notifyError,
  notifySuccess,
  SpecimenTypeUtils,
} from 'src/utils';

const FORM_CONSTANTS = {
  WIDTH: '840px',
  MIN_AGE: 18,
  MAX_AGE: 100,
  MAX_PSA: 500,
  MIN_PSA: 0.01,
} as const;

export type UkProstateBiopsyFormData = z.infer<
  typeof UkProstateBiopsyFormSchema
>;

export const UkProstateBiopsyFormSchema = z.object({
  externalId: z.string().min(1, 'Patient ID is required'),
  patientAge: z
    .number({
      error: (issue) => {
        if (issue.code === 'invalid_type') {
          return { message: 'Patient age is required' };
        }

        return null;
      },
    })
    .min(
      FORM_CONSTANTS.MIN_AGE,
      `Acceptable age range for patient is from ${FORM_CONSTANTS.MIN_AGE} to ${FORM_CONSTANTS.MAX_AGE}`,
    )
    .max(
      FORM_CONSTANTS.MAX_AGE,
      `Acceptable age range for patient is from ${FORM_CONSTANTS.MIN_AGE} to ${FORM_CONSTANTS.MAX_AGE}`,
    )
    .nullable()
    .superRefine((val, ctx) => {
      if (val === null) {
        ctx.addIssue({ code: 'custom', message: 'Patient age is required' });
      }
    }),
  ...REQUIRED_GLEASON_SCORE_FIELDS_SCHEMA.shape,
  baselinePsa: z
    .number({
      error: (issue) => {
        if (issue.code === 'invalid_type') {
          return { message: 'Baseline PSA is required' };
        }
        return null;
      },
    })
    .min(
      FORM_CONSTANTS.MIN_PSA,
      `Baseline PSA must be at least ${FORM_CONSTANTS.MIN_PSA} ng/mL`,
    )
    .max(
      FORM_CONSTANTS.MAX_PSA,
      `Baseline PSA must not exceed ${FORM_CONSTANTS.MAX_PSA} ng/mL`,
    )
    .nullable()
    .superRefine((val, ctx) => {
      if (val === null) {
        ctx.addIssue({ code: 'custom', message: 'Baseline PSA is required' });
      }
    }),
  tStage: z.enum(PROSTATE_BIOPSY_T_STAGES, { error: 'T-Stage is required' }),
  nccnRiskCategory: z.enum(Object.values(NccnRiskCategory), {
    error: 'NCCN Risk Category is required',
  }),
});

// Custom resolver
const createCustomResolver =
  () =>
  async (
    data: UkProstateBiopsyFormData,
    context: unknown,
    options: ResolverOptions<UkProstateBiopsyFormData>,
  ) => {
    const zodResult = await zodResolver(UkProstateBiopsyFormSchema)(
      data,
      context,
      options,
    );

    // Manual NCCN cross-field validation that runs regardless of other field states
    const customErrors = { ...zodResult.errors };

    // Only run NCCN cross-field validation if NCCN field has a value
    if (data.nccnRiskCategory) {
      const validation = NccnRiskCategoryUtils.validateNccnRiskRules(
        data.nccnRiskCategory as NccnRiskCategory,
        data.primaryGleasonScore,
        data.secondaryGleasonScore,
        data.baselinePsa ?? undefined,
        data.tStage as TStage,
      );

      if (!validation.isValid) {
        customErrors.nccnRiskCategory = {
          type: 'custom',
          message:
            validation.errorMessage || 'NCCN Risk Category validation failed',
        };
      }
    }

    return {
      values: data,
      errors: customErrors,
    } as ResolverResult<UkProstateBiopsyFormData>;
  };

// Helper function to create default form values
const createDefaultFormValues = (
  initialOrder: SamdOrderDetailFieldsFragment,
): UkProstateBiopsyFormData => ({
  externalId: initialOrder.externalId?.toString() ?? '',
  patientAge: initialOrder.patientAge ?? null,
  primaryGleasonScore: (initialOrder.primaryGleasonScore?.toString() ?? '') as
    | '3'
    | '4'
    | '5',
  secondaryGleasonScore: (initialOrder.secondaryGleasonScore?.toString() ??
    '') as '3' | '4' | '5',
  baselinePsa: initialOrder.psa ?? null,
  tStage: (initialOrder.tStage || '') as TStage,
  nccnRiskCategory: (initialOrder.nccnRiskCategory || '') as NccnRiskCategory,
});

const PatientAgeField: FC<FormFieldProps<UkProstateBiopsyFormData>> = ({
  control,
  errors,
  trigger,
}) => {
  return (
    <Controller
      name='patientAge'
      control={control}
      render={({ field }) => (
        <IntegerInput
          required
          label='Age at Diagnosis'
          data-testid='patientAge'
          {...field}
          error={!!errors.patientAge}
          helperText={errors.patientAge?.message?.toString() ?? ' '}
          min={FORM_CONSTANTS.MIN_AGE}
          max={FORM_CONSTANTS.MAX_AGE}
          onChange={(value) => {
            field.onChange(value);
            trigger?.('patientAge');
          }}
        />
      )}
    />
  );
};

const BaselinePsaField: FC<FormFieldProps<UkProstateBiopsyFormData>> = ({
  control,
  errors,
  trigger,
  getValues,
}) => {
  return (
    <Controller
      name='baselinePsa'
      control={control}
      render={({ field }) => (
        <DecimalInput
          required
          label='Baseline PSA (ng/mL)'
          data-testid='baselinePsa'
          {...field}
          error={!!errors.baselinePsa}
          validationText={errors.baselinePsa?.message}
          decimalPlaces={2}
          min={FORM_CONSTANTS.MIN_PSA}
          max={FORM_CONSTANTS.MAX_PSA}
          onChange={async (value) => {
            field.onChange(value);
            await trigger?.('baselinePsa');
            // Trigger NCCN validation only if NCCN field has a value (avoid premature required validation)
            if (trigger && getValues) {
              const currentNccn = getValues().nccnRiskCategory;
              if (currentNccn) {
                await trigger('nccnRiskCategory');
              }
            }
          }}
        />
      )}
    />
  );
};

const TStageAndPsaFields: FC<FormFieldProps<UkProstateBiopsyFormData>> = ({
  control,
  errors,
  trigger,
  getValues,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      gap: { xs: 2, md: 3 },
    }}
  >
    <Box
      sx={{
        flex: 1,
      }}
    >
      <BaselinePsaField
        control={control}
        errors={errors}
        trigger={trigger}
        getValues={getValues}
      />
    </Box>
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Controller
        name='tStage'
        control={control}
        render={({ field }) => (
          <SelectList
            required
            labelId='t-stage-label'
            data-testid='t-stage'
            label='T-stage'
            {...field}
            options={getProstateBiopsyTStageOptions(false, false)}
            validationText={errors.tStage?.message?.toString() ?? ''}
            onChange={async (value) => {
              field.onChange(value);
              // Trigger NCCN validation only if NCCN field has a value (avoid premature required validation)
              if (trigger && getValues) {
                const currentNccn = getValues().nccnRiskCategory;
                if (currentNccn) {
                  await trigger('nccnRiskCategory');
                }
              }
            }}
          />
        )}
      />
    </Box>
  </Box>
);

const NccnRiskCategoryField: FC<FormFieldProps<UkProstateBiopsyFormData>> = ({
  control,
  errors,
  trigger,
}) => {
  // Order NCCN Risk Categories from lowest to highest risk
  const NCCN_RISK_CATEGORY_OPTIONS = [
    NccnRiskCategory.VeryLow,
    NccnRiskCategory.Low,
    NccnRiskCategory.IntermediateFavorable,
    NccnRiskCategory.IntermediateUnfavorable,
    NccnRiskCategory.High,
    NccnRiskCategory.VeryHigh,
  ].map((category) => ({
    value: category,
    name: NccnRiskCategoryUtils.getNccnRiskCategoryName(category),
  }));

  return (
    <Controller
      name='nccnRiskCategory'
      control={control}
      render={({ field }) => (
        <RadioGroupField
          required
          row
          name={field.name}
          label='NCCN Risk Category'
          value={field.value}
          onBlur={field.onBlur}
          options={NCCN_RISK_CATEGORY_OPTIONS}
          validationText={errors.nccnRiskCategory?.message?.toString() ?? ''}
          onChange={async (event) => {
            field.onChange(event);
            // Manually trigger validation to clear errors
            if (event.target.value && trigger) {
              await trigger('nccnRiskCategory');
            }
          }}
        />
      )}
    />
  );
};

export const UkSamdProstateBiopsyOrderForm: FC<{
  initialOrder: SamdOrderDetailFieldsFragment;
  onClose: () => void;
}> = ({ initialOrder, onClose }) => {
  const theme = useTheme();
  const fetcher = useFetcher();
  const location = useLocation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
  } = useForm<UkProstateBiopsyFormData>({
    resolver: createCustomResolver(),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: createDefaultFormValues(initialOrder),
  });

  // Convert form data to Order object
  const getOrderFromFormData =
    useCallback((): SamdOrderDetailFieldsFragment => {
      const formData = getValues();

      return {
        ...initialOrder,
        externalId: emptyToUndefined(formData.externalId) ?? null,
        patientAge: formData.patientAge,
        primaryGleasonScore: safeParseInt(formData.primaryGleasonScore),
        secondaryGleasonScore: safeParseInt(formData.secondaryGleasonScore),
        psa: formData.baselinePsa,
        tStage: safeEmptyToUndefined(formData.tStage) as TStage | undefined,
        nccnRiskCategory: safeEmptyToUndefined(formData.nccnRiskCategory) as
          | NccnRiskCategory
          | undefined,
        specimenType: SpecimenType.ProstateBiopsy,
        status: initialOrder.status,
      } as SamdOrderDetailFieldsFragment;
    }, [getValues, initialOrder]);

  // Trigger validation for edit forms to show validation errors immediately
  useEffect(() => {
    if (initialOrder.id) {
      trigger();
    }
  }, [initialOrder, trigger]);

  // Handle action results
  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
        if (result.closeDialog) {
          onClose();
        }
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state, onClose]);

  const isNewOrder = !initialOrder.id;
  const isDraftOrder = initialOrder.status === OrderStatus.Draft;

  const handleSaveOrder = async () => {
    const orderData = getOrderFromFormData();

    if (!orderData.id) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.CREATE,
        location,
        orderData,
      );
    } else {
      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE,
        location,
        orderData,
      );
    }
  };

  const handleSubmitOrder = async () => {
    const orderData = getOrderFromFormData();

    // Decide between submit and resubmit based on order status
    // Use RESUBMIT for orders that have been submitted before (not Draft status)
    const action =
      orderData.status === OrderStatus.Draft
        ? OrderAction.SUBMIT
        : OrderAction.RESUBMIT;

    submitOrderAction(fetcher.submit, action, location, orderData);
  };

  const handleDiscard = async () => {
    // Unsaved orders do not need to be deleted because they don't exist yet
    if (isDraftOrder && !isNewOrder) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.DELETE,
        location,
        undefined,
      );
    } else {
      notifySuccess('Order discarded successfully.');
      onClose();
    }
  };

  const formTitle = `${isNewOrder ? 'Create' : 'Edit'} ${SpecimenTypeUtils.getSpecimenTypeName(SpecimenType.ProstateBiopsy)} Test Order`;

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(handleSubmitOrder)}
      noValidate
      sx={{
        width: '100%',
        maxWidth: FORM_CONSTANTS.WIDTH,
        px: { xs: 0, sm: 2, md: 0 },
      }}
    >
      <Typography
        variant='h5'
        sx={{
          display: 'block',
          fontWeight: theme.typography.fontWeightBold,

          fontSize: {
            xs: theme.typography.h6.fontSize,
            sm: theme.typography.h5.fontSize,
          },
        }}
      >
        {formTitle}
      </Typography>
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 2,
        }}
      >
        *Indicates a required field
      </Typography>
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 1,
        }}
      >
        Protected Health Information, such as patient name, date of birth, or
        medical record number, should not be entered into this web portal.
      </Typography>
      <Stack spacing={{ xs: 2, sm: 2 }}>
        <PatientIdField control={control} errors={errors} />
        <PatientAgeField control={control} errors={errors} trigger={trigger} />
        <GleasonScoreFields
          control={control}
          errors={errors}
          trigger={trigger}
          onPrimaryChange={async (event) => {
            // Trigger NCCN validation only if NCCN field has a value (avoid premature required validation)
            const nccnRiskCategory = getValues().nccnRiskCategory;
            if (event.target.value && nccnRiskCategory) {
              await trigger('nccnRiskCategory');
            }
          }}
          onSecondaryChange={async (event) => {
            // Trigger NCCN validation only if NCCN field has a value (avoid premature required validation)
            const nccnRiskCategory = getValues().nccnRiskCategory;
            if (event.target.value && nccnRiskCategory) {
              await trigger('nccnRiskCategory');
            }
          }}
        />
        <TStageAndPsaFields
          control={control}
          errors={errors}
          trigger={trigger}
          getValues={getValues}
        />
        <NccnRiskCategoryField
          control={control}
          errors={errors}
          trigger={trigger}
        />
        <FormActions
          showSubmitButton={true}
          showSaveButton={false}
          showDraftActionButtons={isDraftOrder || isNewOrder}
          onValidateBeforeSubmit={async () => trigger()}
          onConfirmSubmit={handleSubmitOrder}
          onSaveAsDraft={handleSaveOrder}
          onDiscard={handleDiscard}
          requireDiscardConfirmation={!isNewOrder}
          onDiscardSuccess={() => {
            notifySuccess('Order discarded successfully.');
            onClose();
          }}
        />
      </Stack>
    </Box>
  );
};
